import React, { useEffect, useState, useRef, useCallback } from 'react';
import { sanitizeText } from '../../utils/sanitize';
import styles from './Toast.module.css';

const Toast = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const remainingTimeRef = useRef(toast.duration);
  const pauseStartedAtRef = useRef(null);
  const isDismissedRef = useRef(false);

  const handleRemove = useCallback(() => {
    if (isDismissedRef.current) return; // Prevent double dismiss
    isDismissedRef.current = true;
    
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300); // Match exit animation duration
  }, [onRemove, toast.id]);

  // Handle auto-dismiss timer
  useEffect(() => {
    if (!toast.duration || toast.duration === 0) {
      // No auto-dismiss for duration 0 (error toasts)
      return;
    }

    const startTimer = () => {
      if (remainingTimeRef.current <= 0) {
        handleRemove();
        return;
      }
      
      timerRef.current = setTimeout(handleRemove, remainingTimeRef.current);
    };

    if (!isPaused) {
      startTimer();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [toast.duration, isPaused, handleRemove]);

  const handleMouseEnter = () => {
    if (toast.duration && toast.duration > 0 && !isDismissedRef.current) {
      // Clear timer and pause
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      
      // Record when pause started
      pauseStartedAtRef.current = Date.now();
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (toast.duration && toast.duration > 0 && !isDismissedRef.current) {
      // Calculate how long we were paused
      if (pauseStartedAtRef.current) {
        const pauseDuration = Date.now() - pauseStartedAtRef.current;
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - pauseDuration);
        pauseStartedAtRef.current = null;
      }
      
      setIsPaused(false);
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const showCount = toast.count && toast.count > 1;

  return (
    <div
      className={`${styles.toast} ${styles[toast.type]} ${isExiting ? styles.exit : ''}`}
      role="status"
      aria-atomic="true"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <button
        className={styles.closeButton}
        onClick={handleRemove}
        aria-label="Close notification"
        type="button"
      >
        ×
      </button>
      <span className={styles.icon} aria-hidden="true">{getIcon()}</span>
      <span className={styles.message}>
        {sanitizeText(toast.message)}
        {showCount && (
          <span className={styles.count} aria-label={`${toast.count} occurrences`}>
            ×{toast.count}
          </span>
        )}
      </span>
    </div>
  );
};

export default Toast;